import { useMutation, useReactiveVar } from '@apollo/client'
import { ContentItem } from '@deltek/specification-client/core/Models/contentItem'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { DragEndEvent } from '@dnd-kit/core'
import Button from '@material-ui/core/Button'
import Tabs from '@material-ui/core/Tabs'
import Typography from '@material-ui/core/Typography'
import { Theme, makeStyles } from '@material-ui/core/styles'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import { PromoteDemotContentItem } from 'api/graphQL/mutations/projectProductFamily.mutations'
import {
    resetOnFocusProperty,
    workspaceProperty,
} from 'api/graphQL/reactiveVars/property'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import {
    isOmWorkspaceUser,
    updatePartDTV,
} from 'api/graphQL/reactiveVars/workspaceContent'
import { showHideToggles } from 'api/graphQL/reactiveVars/workspaceShowHideToggles'
import {
    setAreSharedPropertiesVisible,
    workspaceToggles,
} from 'api/graphQL/reactiveVars/workspaceToggles'
import clsx from 'clsx'
import { TrackChangeType } from 'models/changes'
import React from 'react'
import { useAELocalStorageWriteOnly } from 'shared/utils/localStrorageUtils'
import { AreaContext } from './AreaContext'
import DragDropArea from './DragDropArea'
import { DragDropAreaProvider } from './DragDropAreaContext'
import { TabsContext } from './TabsContext'
import {
    DuplicateKeyErrorMessage,
    PromoteDemoteBaseContentError,
    checkContentCauseKeyDuplicates,
    checkKeyDuplicates,
    extractKey,
    isContentAllowedToBeDemoted,
    isContentAllowedToBePromoted,
    isContentMovableToParent,
} from './addUtils'
import {
    CheckContentCauseKeyDuplicatesRequirements,
    NormalizedChildDocumentTemplateViewItem,
    NormalizedDocumentTemplateViewItem,
    PromoteDemotContentItemRequirements,
    PromoteDemoteRequirements,
} from './model'
import {
    WorkspaceTabTitles,
    findInTemplates,
    findItemInTemplates,
    getTopicContentItemsDetailForSustainabilityContent,
    isOutlineMaintained,
    mapInTemplates,
    setPlacementForBullets,
    updatedFullViewArticles,
} from './utils'

import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import { DocumentTemplateView } from '@deltek/specification-client/core/Models/documentTemplate'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { Tab } from '@material-ui/core'
import { workspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import { templateData } from 'api/graphQL/reactiveVars/workspaceActions'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import useGlobalSnackbar from 'shared/utils/useGlobalSnackbar'
import { LoadingSkeleton } from '../LoadingSkeleton'
import { PremiumInfoIconTooltip } from '../PremiumInfoIconTooltip'

interface CustomTabsProps {
    baseNotes?: NormalizedDocumentTemplateViewItem[]
    general?: NormalizedDocumentTemplateViewItem
    products?: NormalizedDocumentTemplateViewItem
    execution?: NormalizedDocumentTemplateViewItem
    loading: boolean
    contentTeamFromStudioPage?: boolean
    isDraftMode?: boolean
    selectedTopic?: Partial<ProductFamilyStudioManagement>
    tab: number
    projectProductFamilyId?: string
    documentTemplateView: Partial<DocumentTemplateView> | null
    isAddProhibited?: boolean
    dtv: Partial<DocumentTemplateView> | null
    addQueueSib?: string[] | undefined
    contentItemNodes?: Partial<ContentItem>[]
    elementIdOnEdit?: string | undefined
    isAddOngoing?: boolean
    isNewAddOnQueue?: boolean
    familyAddedProductTypes?: ProjectElementNode[]
}
const a11yProps = (index: number) => {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `droppable-area`,
        'data-testid': `customTab${index}`,
    }
}

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        flexGrow: 1,
        backgroundColor: theme.palette.background.paper,
        height: '100%',
        overflowY: 'hidden',
        paddingTop: pxToRem(16),
    },
    tabs: {
        flexWrap: 'wrap',
    },
    contentContainerTopic: {
        height: 'inherit',
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollBehavior: 'smooth',
        marginTop: theme.spacing(0.5),
        paddingRight: theme.spacing(0.5),
        '@media (max-width: 1460px)': {
            height: 'calc(100% - 135px)',
        },
        '@media (max-width: 1024px)': {
            width: 250,
            '& .wrappedDraggable': {
                minWidth: 450,
            },
        },
        paddingTop: theme.spacing(1.5),
        marginLeft: theme.spacing(0.5),
    },
    tabRoot: {
        borderRadius: '4px 4px 0px 0px',
        marginRight: theme.spacing(0.5),
        marginLeft: theme.spacing(0.5),
        '@media (min-width: 757px)': {
            minWidth: 'unset',
        },
        '@media (min-width: 1600px)': {
            minWidth: 120,
        },
        '@media (min-width: 1800px)': {
            minWidth: 160,
        },
    },
    tabActive: {
        backgroundColor: '#304959',
        color: theme.palette.common.white,
    },
    contentContainer: {
        maxHeight: 'calc(100% - 81px)',
        height: 'auto',
        overflowY: 'auto',
        scrollBehavior: 'smooth',
        marginTop: theme.spacing(2),
        paddingRight: theme.spacing(0.5),
        '@media (max-width: 1460px)': {
            height: 'calc(100% - 135px)',
        },
        '@media (max-width: 1024px)': {
            width: 250,
            '& .wrappedDraggable': {
                minWidth: 450,
            },
        },
    },
    contentContainerFullView: {
        height: '97%',
        overflowY: 'auto',
        scrollBehavior: 'smooth',
        marginTop: theme.spacing(0),
        paddingTop: theme.spacing(1.6),
        paddingRight: theme.spacing(0.5),
        paddingBottom: theme.spacing(3),
        maxHeight: '100%',
        borderTop: '1px solid #DADADA',
    },
    contentContainerCenter: {
        textAlign: 'center',
    },
    expandCollapseContainer: {
        justifyContent: 'flex-end',
        display: 'flex',
        '& button:last-child': {
            marginLeft: theme.spacing(1),
        },
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
        marginRight: theme.spacing(1),
    },
    expandCollapseButton: {
        border: '1px solid #c9c9c9',
        borderRadius: theme.spacing(2.5),
        color: '#04325B',
        lineHeight: 1.43,
        padding: '5px 10px',
        whiteSpace: 'nowrap',
    },
    buttonLabel: {
        lineHeight: '16px',
    },
    tabContainer: {
        paddingTop: theme.spacing(2),
        borderBottom: '1px solid #DADADA',
        flexFlow: 'row-reverse',
        '@media (max-width: 1460px)': {
            flexFlow: 'column',
        },
    },
    tabContainerOM: { borderBottom: 'none', paddingTop: pxToRem(10) },
    tabsSMMFullView: { borderBottom: 'none', paddingTop: 0 },
    tabsFixed: {
        position: 'relative',
    },
    tabsIndicator: {
        border: 'none',
        height: 0,
    },
    grayText: {
        color: theme.palette.grey[500],
        fontSize: pxToRem(14),
        textAlign: 'center',
    },
    notUsedContainer: {
        textAlign: 'center',
    },
    notUsedSMM: {
        textAlign: 'start',
    },
    partLabel: {
        fontSize: pxToRem(24),
        color: '#394659',
        marginBottom: theme.spacing(1.25),
    },
    expandCollapseSMMWrapper: {
        display: 'flex',
        alignItems: 'center',
    },
    iconContainer: {
        marginTop: theme.spacing(0.75),
        marginRight: theme.spacing(0.75),
    },
    loadingSkeletonContainer: {
        padding: '16px 0',
    },
    loadingSkeletonItem: {
        marginBottom: theme.spacing(0.5),
    },
    loadingSkeletonContent: {
        paddingLeft: theme.spacing(2),
    },
    loadingSkeletonLine: {
        marginBottom: theme.spacing(1),
    },
}))

const CustomTabs: React.FC<CustomTabsProps> = ({
    baseNotes,
    general,
    products,
    execution,
    loading,
    contentTeamFromStudioPage,
    isDraftMode,
    selectedTopic,
    tab,
    projectProductFamilyId,
    documentTemplateView,
    isAddProhibited,
    dtv,
    addQueueSib,
    contentItemNodes,
    elementIdOnEdit,
    isAddOngoing,
    isNewAddOnQueue,
    familyAddedProductTypes,
}) => {
    const {
        type,
        setTab,
        productFamily,
        nugget,
        handleExpandClick,
        handleFamilyReorderTemplates,
        handleProductTypeReorderTemplates,
        handleIsContentItemReOrdered,
        isContentItemReOrdered,
        updateExpandState,
        projectId,
    } = React.useContext(AreaContext)
    const { currentUserActiveFirm } = React.useContext(AppBarContext)
    const { setDragStarted } = React.useContext(TabsContext)
    const classes = useStyles()

    const { showGlobalSnackbar } = useGlobalSnackbar()

    const { selectedElementNode: selectedProjectNode } = useReactiveVar(
        workspaceSelectedElement
    )
    const { workspaceTemplateData } = useReactiveVar(templateData)

    const { propertyToFocus } = useReactiveVar(workspaceProperty)

    const { areSharedPropertiesVisible: areSharedVisible } = useReactiveVar(
        workspaceToggles
    )

    const {
        currentUserInfo: { kpUserId },
    } = useReactiveVar(userInfo)
    const { isTrackChangeToggleOn: isTrackChangeToggleOn } = useReactiveVar(
        showHideToggles
    )
    const isOMUser = useReactiveVar(isOmWorkspaceUser)

    const isBPWorkspace = React.useMemo(
        () =>
            isOMUser &&
            projectId === currentUserActiveFirm?.bestPracticeProjectId,
        [currentUserActiveFirm, isOMUser]
    )

    const [promotedItemId, setPromotedItemId] = React.useState<
        string | undefined
    >()

    const [promoteDemoteContentItem] = useMutation(PromoteDemotContentItem, {
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
            resetPromoteDemote()
            updatePartDTV(data?.promoteDemoteContent, documentTemplateView!)
            setPromotedItemId(undefined)
            showGlobalSnackbar?.({
                variables: {
                    message: 'Changes Saved Successfully',
                    variant: 'success',
                    id: `PromoteDemoteContentItemSuccess`,
                },
            })
        },
        onError: () => {
            resetPromoteDemote()
            showGlobalSnackbar({
                variables: {
                    message: `There was a problem promoting/demoting the content`,
                    variant: 'error',
                    id: 'PromoteDemote-error',
                },
            })
        },
    })

    const [item, setItem] = React.useState<
        NormalizedDocumentTemplateViewItem | undefined
    >(undefined)
    const articlesOnProductFamily = React.useMemo(() => {
        return updatedFullViewArticles(productFamily)
    }, [productFamily])
    const [allFullViewArticles, setAllFullViewArticles] = React.useState<
        NormalizedDocumentTemplateViewItem[] | undefined
    >()
    const isProductTypeElement = React.useMemo(() => {
        return selectedProjectNode?.elementType === 'PRODUCTTYPE'
    }, [selectedProjectNode])
    const [localTab, setLocalTab] = React.useState<number>(1)

    const [itemChildren, setItemChildren] = React.useState<
        NormalizedChildDocumentTemplateViewItem[]
    >()

    const [autoCollapsedId, setAutoCollapsedId] = React.useState<
        string | undefined
    >()

    const [selectedContent, setSelectedContent] = React.useState<string>()
    const [promoteDemoteLoading, setPromoteDemoteLoading] = React.useState<
        boolean
    >(false)

    const [isTooltipOpen, setTooltipOpen] = React.useState(false)

    const handleChange = React.useCallback(
        (_event: React.ChangeEvent<{}>, newValue: number) => {
            handleIsContentItemReOrdered?.(false)
            setTab?.(newValue)
            setLocalTab(newValue)
            resetOnFocusProperty()
        },
        [setTab, handleIsContentItemReOrdered, resetOnFocusProperty]
    )

    const handleDragEnd = React.useCallback(
        (result: DragEndEvent) => {
            setDragStarted?.()
            const { active, over } = result
            if (
                !over ||
                !item?.children ||
                active?.data?.current?.sortable?.type !==
                    over?.data?.current?.sortable?.type ||
                over?.data?.current?.sortable?.disabled ||
                active?.data?.current?.disabled ||
                !active?.data?.current?.sortable?.containerId ||
                !over?.data?.current?.sortable?.containerId
            ) {
                return
            }

            let sourceIndex: number = active?.data?.current?.sortable?.index
            let destIndex: number = over?.data?.current?.sortable?.index
            const activeContainer: string =
                active?.data?.current?.sortable?.containerId
            const overContainer: string =
                over?.data?.current?.sortable?.containerId || over.id

            const isForArticles = activeContainer.includes('dragDropArea')
            const isAfter =
                over.id.substring(0, over.id?.indexOf('-')) === 'after'
            const sourceId = active.id.substring(active.id?.indexOf('-') + 1)
            const destinationId = over.id.substring(over.id?.indexOf('-') + 1)

            if (
                destIndex === undefined ||
                (!isForArticles &&
                    sourceIndex === destIndex &&
                    activeContainer === overContainer &&
                    !over?.data?.current?.sortable?.isRegroup) ||
                (sourceIndex === destIndex &&
                    !over?.data?.current?.sortable?.isRegroup &&
                    activeContainer === overContainer) ||
                (active.id.includes(destinationId) &&
                    !over?.data?.current?.sortable?.isRegroup)
            )
                return

            let newItems: NormalizedDocumentTemplateViewItem[] = []
            let reorderedItems: NormalizedDocumentTemplateViewItem[] = []

            const {
                srcIndexFromChildren,
                destIndexFromChildren,
            } = item.children.reduce(
                (
                    acc: {
                        srcIndexFromChildren: number
                        destIndexFromChildren: number
                    },
                    curr: NormalizedChildDocumentTemplateViewItem,
                    index: number
                ) => {
                    if (curr.id == sourceId) {
                        acc.srcIndexFromChildren = index
                    }
                    if (curr.id == destinationId) {
                        acc.destIndexFromChildren = index
                    }
                    return acc
                },
                { srcIndexFromChildren: 0, destIndexFromChildren: 0 }
            )
            if (activeContainer === overContainer) {
                if (isForArticles) {
                    const articleToIndex =
                        srcIndexFromChildren < destIndexFromChildren && !isAfter
                            ? destIndexFromChildren - 1
                            : destIndexFromChildren
                    reorderedItems = setPlacementForBullets(
                        item.children,
                        articleToIndex,
                        srcIndexFromChildren
                    )
                } else {
                    const parentId = activeContainer.substring(
                        activeContainer?.indexOf('-') + 1
                    )
                    const itemSubItemMap = findInTemplates(
                        item.children,
                        parentId
                    ) as NormalizedDocumentTemplateViewItem[]

                    if (over?.data?.current?.sortable?.isRegroup) {
                        let newParentId = !over.id?.includes('before')
                            ? over.id.substring(over.id?.indexOf('-') + 1)
                            : itemSubItemMap?.[
                                  over?.data?.current?.sortable?.index - 1
                              ]?.id
                        const propertyToMove = itemSubItemMap?.[sourceIndex]

                        if (newParentId === propertyToMove?.id) {
                            newParentId = itemSubItemMap?.[sourceIndex - 1]?.id
                        }

                        const parent = findItemInTemplates(
                            item.children,
                            newParentId
                        )

                        if (
                            parent?.isBOD ||
                            parent?.isManufacturers ||
                            !newParentId ||
                            !parent
                        ) {
                            showGlobalSnackbar({
                                variables: {
                                    message:
                                        'The action could not be completed. Be sure to maintain a consistent outline structure.',
                                    variant: 'error',
                                    id: `PromoteDemoteError`,
                                },
                            })
                            return
                        }
                        const removedInOld = setPlacementForBullets(
                            itemSubItemMap.filter((item, index) => {
                                const actualSourceIndex =
                                    sourceIndex === itemSubItemMap.length
                                        ? sourceIndex - 1
                                        : sourceIndex
                                return index !== actualSourceIndex
                            }),
                            sourceIndex
                        )
                        const updatedItems = mapInTemplates(
                            item.children,
                            parentId,
                            removedInOld
                        )

                        const newSibs = findInTemplates(
                            updatedItems,
                            newParentId
                        )

                        const newLevelOfMovedProperty =
                            newSibs?.length > 0
                                ? newSibs?.[0]?.level
                                : (parent?.level ?? 0) + 1
                        const isOutlineRuined =
                            (newLevelOfMovedProperty > 6 ||
                                (propertyToMove?.children?.length > 0 &&
                                    !isOutlineMaintained(
                                        propertyToMove?.children,
                                        newLevelOfMovedProperty + 1
                                    ))) ??
                            false
                        if (isOutlineRuined) {
                            showGlobalSnackbar({
                                variables: {
                                    message:
                                        'The action could not be completed. Be sure to maintain a consistent outline structure.',
                                    variant: 'error',
                                    id: `PromoteDemoteError`,
                                },
                            })
                            return
                        }
                        const regroupedItems = setPlacementForBullets(
                            [...newSibs, propertyToMove],
                            0
                        ).map((item) => {
                            return { ...item, level: newLevelOfMovedProperty }
                        })

                        newItems = mapInTemplates(
                            updatedItems,
                            newParentId,
                            regroupedItems
                        )

                        const contentItemId = propertyToMove?.contentItem?.id
                        const parentContentItemId =
                            parent?.contentItem?.id ??
                            parent?.documentTemplateViewContents?.[0]
                                ?.documentTemplateViewContentItems?.[0]?.id
                        setPromotedItemId(
                            propertyToMove?.documentTemplateViewItem?.itemId
                        )
                        promoteDemoteContentItem({
                            variables: {
                                promoteDemonteContentInput: {
                                    contentId:
                                        propertyToMove?.documentTemplateViewItem
                                            ?.itemId,
                                    documentTemplateViewId:
                                        propertyToMove?.documentTemplateView
                                            ?.id,
                                    contentItemId,
                                    parentContentId:
                                        parent?.documentTemplateViewItem
                                            ?.itemId,
                                    parentContentItemId,
                                    productFamilyId: projectProductFamilyId,
                                    order:
                                        regroupedItems?.findIndex(
                                            (item) =>
                                                item?.id === propertyToMove?.id
                                        ) ?? 0,
                                    projectNuggetId: nugget?.id,
                                },
                            },
                        })
                    } else {
                        const propertyToIndex =
                            sourceIndex < destIndex ? destIndex - 1 : destIndex

                        reorderedItems = setPlacementForBullets(
                            type === 'ProductType' && !areSharedVisible
                                ? itemSubItemMap?.filter(
                                      (child) => !child?.isSharedProperty
                                  )
                                : itemSubItemMap,
                            propertyToIndex,
                            sourceIndex
                        )

                        newItems = mapInTemplates(
                            item.children,
                            parentId,
                            reorderedItems
                        )

                        if (type === 'ProductType' && !areSharedVisible) {
                            newItems = newItems?.filter(
                                (child) => !child?.isSharedProductTypeProperty
                            )
                        }
                    }
                }

                setItemChildren(
                    reorderedItems[0]?.level === 1 ? reorderedItems : newItems
                )
                if (type === 'All') {
                    setAllFullViewArticles(
                        reorderedItems[0]?.level === 1
                            ? reorderedItems
                            : newItems
                    )
                }
                if (!over?.data?.current?.sortable?.isRegroup) {
                    if (
                        type === 'Family' ||
                        type === 'Sustainability Topic' ||
                        (type === 'All' && !isProductTypeElement)
                    ) {
                        handleFamilyReorderTemplates?.({
                            parentId:
                                reorderedItems?.[destIndexFromChildren]
                                    ?.parentId ?? '',
                            documentTemplateViewId:
                                reorderedItems?.[destIndexFromChildren]
                                    ?.documentTemplateView?.id,
                            isContentItem:
                                reorderedItems?.[destIndexFromChildren]
                                    ?.type === 'ContentItem',
                            projectProductFamilyId: productFamily?.id,
                            viewContentItemReorderInputs: reorderedItems.map(
                                (each, index) => ({
                                    contentItemId: each.contentItem?.id,
                                    order: index,
                                })
                            ),
                        })
                    } else if (
                        type === 'ProductType' ||
                        (type === 'All' && isProductTypeElement)
                    ) {
                        handleProductTypeReorderTemplates?.({
                            parentId:
                                reorderedItems?.[destIndexFromChildren]
                                    ?.parentId ?? '',
                            documentTemplateViewId:
                                reorderedItems?.[destIndexFromChildren]
                                    ?.documentTemplateView?.id,
                            isContentItem:
                                reorderedItems?.[destIndexFromChildren]
                                    ?.type === 'ContentItem',
                            projectNuggetId: nugget?.id,
                            viewContentItemReorderInputs: reorderedItems.map(
                                (each, index) => ({
                                    contentItemId: each.contentItem?.id,
                                    order: index,
                                })
                            ),
                        })
                    }
                }
            } else {
                //promote/demote
                const oldParentId = activeContainer.substring(
                    activeContainer?.indexOf('-') + 1
                )
                let newIdOfParent = overContainer.substring(
                    overContainer?.indexOf('-') + 1
                )
                if (over?.data?.current?.sortable?.isRegroup) {
                    newIdOfParent = !over.id?.includes('before')
                        ? over.id.substring(over.id?.indexOf('-') + 1)
                        : findInTemplates(item.children, newIdOfParent)?.[
                              over?.data?.current?.sortable?.index - 1
                          ]?.id
                }

                const propertyId = active?.id?.substring(
                    active?.id?.indexOf('-') + 1
                )
                const oldParentChildren = findInTemplates(
                    item.children,
                    oldParentId
                ) as NormalizedDocumentTemplateViewItem[]
                const actualParent = findItemInTemplates(
                    item.children,
                    newIdOfParent
                )
                const newSiblingId = over?.id?.substring(
                    over?.id?.indexOf('-') + 1
                )

                let orderedIndex = destIndex
                if (
                    type === 'ProductType' ||
                    (type === 'All' && isProductTypeElement)
                ) {
                    const siblingsToBe = findInTemplates(
                        item.children,
                        newIdOfParent
                    ) as NormalizedDocumentTemplateViewItem[]

                    sourceIndex =
                        oldParentChildren?.findIndex(
                            (item) => item?.id === propertyId
                        ) ?? sourceIndex

                    const actualSiblingIndex =
                        siblingsToBe?.findIndex(
                            (child) => child?.id === newSiblingId
                        ) ?? 0
                    destIndex = !over.id?.includes('before')
                        ? actualSiblingIndex + 1
                        : actualSiblingIndex
                    orderedIndex = destIndex
                    if (actualParent?.isProductGroupHasMultipleProductTypes) {
                        const siblingOrder =
                            siblingsToBe?.find(
                                (child) => child?.id === newSiblingId
                            )?.order ?? 0
                        orderedIndex = !over.id?.includes('before')
                            ? siblingOrder + 1
                            : siblingOrder
                    }
                }
                //remove old move
                const removedInOldParent = setPlacementForBullets(
                    oldParentChildren.filter((item, index) => {
                        const actualSourceIndex =
                            sourceIndex === oldParentChildren.length
                                ? sourceIndex - 1
                                : sourceIndex
                        return index !== actualSourceIndex
                    }),
                    sourceIndex
                )
                const removedInOldParentItems = mapInTemplates(
                    item.children,
                    oldParentId,
                    removedInOldParent
                )

                const newParentChildren = findInTemplates(
                    removedInOldParentItems,
                    newIdOfParent
                ) as NormalizedDocumentTemplateViewItem[]

                const property =
                    oldParentChildren?.[sourceIndex] ??
                    findItemInTemplates(oldParentChildren, propertyId)

                if (
                    newParentChildren?.length === 0 &&
                    newParentChildren?.length < destIndex &&
                    over?.data?.current?.sortable?.isRegroup
                ) {
                    destIndex = 0
                    orderedIndex = 0
                } else if (
                    !newParentChildren?.find(
                        (sibToBe) => sibToBe?.id === newSiblingId
                    )
                ) {
                    destIndex = newParentChildren?.length!
                    orderedIndex = newParentChildren?.length!
                }
                const newLevelOfToProperty =
                    newParentChildren?.length > 0
                        ? newParentChildren?.[0]?.level
                        : (actualParent?.level ?? 0) + 1
                const isOutlineRuined =
                    (newLevelOfToProperty > 6 ||
                        (property?.children?.length > 0 &&
                            !isOutlineMaintained(
                                property?.children,
                                newLevelOfToProperty + 1
                            ))) ??
                    false
                if (
                    actualParent?.isBOD ||
                    actualParent?.isManufacturers ||
                    !newIdOfParent ||
                    !actualParent ||
                    isOutlineRuined
                ) {
                    showGlobalSnackbar({
                        variables: {
                            message:
                                'The action could not be completed. Be sure to maintain a consistent outline structure.',
                            variant: 'error',
                            id: `PromoteDemoteError`,
                        },
                    })
                    return
                }

                const newChildrenOfNewParent = newParentChildren
                    .slice(0, destIndex)
                    .concat({
                        ...property,
                        level: newParentChildren?.[0]?.level,
                    })
                    .concat(newParentChildren.slice(destIndex))

                if (
                    property?.contentItem?.elements?.some((element) =>
                        element?.text?.includes(':')
                    )
                ) {
                    const propertyKey = extractKey(
                        property,
                        newIdOfParent,
                        removedInOldParentItems
                    )
                    const isCausingDuplicateKey = checkKeyDuplicates(
                        propertyKey as string,
                        false,
                        newParentChildren?.map((item) => {
                            return item?.contentItem
                        }) as Partial<ContentItem>[]
                    )
                    if (isCausingDuplicateKey) {
                        showGlobalSnackbar({
                            variables: {
                                message: DuplicateKeyErrorMessage,
                                variant: 'error',
                                id: `PromoteDemoteError`,
                            },
                        })
                        return
                    }
                }

                reorderedItems = setPlacementForBullets(
                    newChildrenOfNewParent,
                    destIndex
                ).map((item) => {
                    return { ...item, level: newLevelOfToProperty }
                })

                newItems = mapInTemplates(
                    removedInOldParentItems,
                    newIdOfParent,
                    reorderedItems
                )
                if (
                    (type === 'ProductType' && !areSharedVisible) ||
                    (type === 'All' && isProductTypeElement)
                ) {
                    newItems = newItems?.filter(
                        (item) => !item.isSharedProductTypeProperty
                    )
                }

                setItemChildren(
                    reorderedItems[0]?.level === 1 ? reorderedItems : newItems
                )

                const newParentTemplate = findItemInTemplates(
                    item.children,
                    newIdOfParent
                )

                const actualContentItemId = property?.contentItem?.id
                const actualParentContentItemId =
                    newParentTemplate?.contentItem?.id ??
                    newParentTemplate?.documentTemplateViewContents?.[0]
                        ?.documentTemplateViewContentItems?.[0]?.id

                setPromotedItemId(property?.documentTemplateViewItem?.itemId)
                promoteDemoteContentItem({
                    variables: {
                        promoteDemonteContentInput: {
                            contentId:
                                property?.documentTemplateViewItem?.itemId,
                            documentTemplateViewId:
                                property?.documentTemplateView?.id,
                            contentItemId: actualContentItemId,
                            parentContentId:
                                newParentTemplate?.documentTemplateViewItem
                                    ?.itemId,
                            parentContentItemId: actualParentContentItemId,
                            productFamilyId: projectProductFamilyId,
                            order: orderedIndex,
                            projectNuggetId: nugget?.id,
                        },
                    },
                })
                return
            }

            if (isForArticles && autoCollapsedId) {
                updateExpandState?.(autoCollapsedId)
                setAutoCollapsedId(undefined)
            }
        },
        [
            handleFamilyReorderTemplates,
            handleProductTypeReorderTemplates,
            setItemChildren,
            item,
            productFamily,
            projectProductFamilyId,
            nugget,
            type,
            areSharedVisible,
            setDragStarted,
            showGlobalSnackbar,
            updateExpandState,
            promoteDemoteContentItem,
            autoCollapsedId,
            isProductTypeElement,
            allFullViewArticles,
        ]
    )
    const handleExpandAll = React.useCallback(() => {
        handleExpandClick?.(true)
        if (propertyToFocus) {
            resetOnFocusProperty()
        }
    }, [handleExpandClick, propertyToFocus])

    const handleCollapseAll = React.useCallback(() => {
        handleExpandClick?.(false)
        if (propertyToFocus) {
            resetOnFocusProperty()
        }
    }, [handleExpandClick, propertyToFocus])

    const handleClickPremiumIcon = React.useCallback(() => {
        setTooltipOpen(true)
    }, [])

    const handleClickOutsidePremiumIcon = React.useCallback(() => {
        setTooltipOpen(false)
    }, [])

    const getNotUsed = (forceShowNotUsed?: boolean) => {
        const isNotUsed =
            type === 'Family' &&
            (!documentTemplateView ||
                !(documentTemplateView.items ?? []).some(
                    (dtvi) =>
                        dtvi.partType?.toLowerCase() ===
                        WorkspaceTabTitles[localTab].toLowerCase()
                )) &&
            !loading

        if (isNotUsed || forceShowNotUsed) {
            return (
                <div
                    className={clsx(
                        forceShowNotUsed
                            ? classes.notUsedSMM
                            : classes.notUsedContainer,
                        classes.grayText
                    )}
                >
                    Not Used
                </div>
            )
        }

        // This is to check if user is on OM workspace and selected a PT
        // If the user is on 'Product' or 'Execution' tab, then we display the below message
        const isOMWorkspacePT =
            contentTeamFromStudioPage &&
            isOMUser &&
            type === 'ProductType' &&
            [0, 2].includes(localTab)
        if (isOMWorkspacePT) {
            return (
                <div
                    className={clsx(classes.notUsedContainer, classes.grayText)}
                    data-testId="OMNoPTContentInfoText"
                >
                    Please select the Family Level to load Part 1 and Part 3
                    content
                </div>
            )
        }
    }

    //#region Promote/Demote Key Shortcuts
    const resetPromoteDemote = React.useCallback(() => {
        setPromoteDemoteLoading(false)
    }, [])

    const defaultPromoteDemoteSnackBar = React.useCallback(
        (message?: string) => {
            resetPromoteDemote()
            showGlobalSnackbar({
                variables: {
                    message:
                        message ??
                        'The action could not be completed. Be sure to maintain a consistent outline structure.',
                    variant: 'error',
                    id: 'PromoteDemoteError',
                },
            })
        },
        [resetPromoteDemote, showGlobalSnackbar]
    )

    const setPromoteDemoteRequirements = React.useCallback(
        (input?: PromoteDemoteRequirements) => {
            const selectedElementData = input?.content
            const parent = input?.parent
            // Setting parent as undefined for sustainability content when promoting to level 1
            const shouldSetParentAsUndefinedForSustainabilityPromotion =
                input?.action === 'Promote' &&
                parent?.partType === 'ContentGroup' &&
                selectedElementData?.level === 2
            const parentElementData = !shouldSetParentAsUndefinedForSustainabilityPromotion
                ? parent
                : undefined
            const articlesData = itemChildren
            setSelectedContent(selectedElementData?.itemId)
            if (!selectedElementData || promoteDemoteLoading || !articlesData) {
                return
            }
            const isBaseContentPromoteDemoteAllowed =
                contentTeamFromStudioPage || isDraftMode

            // Check if base content
            if (
                !isBaseContentPromoteDemoteAllowed &&
                !selectedElementData.isUserAdded
            ) {
                return defaultPromoteDemoteSnackBar(
                    PromoteDemoteBaseContentError
                )
            }

            setPromoteDemoteLoading(true)

            // Check if content can be promoted/demoted
            let order: number = 0
            if (
                isContentAllowedToBeDemoted(
                    selectedElementData,
                    isBaseContentPromoteDemoteAllowed!
                ) &&
                input?.action === 'Demote'
            ) {
                order = parentElementData?.children.length || 0

                // Check if content don't ruined outline level
                const newLevel = selectedElementData?.level
                    ? selectedElementData?.level + 1
                    : 0
                const isOutlineRuined =
                    (newLevel > 6 ||
                        (selectedElementData?.children &&
                            selectedElementData?.children.length > 0 &&
                            !isOutlineMaintained(
                                selectedElementData?.children ?? [],
                                newLevel + 1
                            ))) ??
                    false
                if (isOutlineRuined) {
                    return defaultPromoteDemoteSnackBar()
                }
            } else if (
                isContentAllowedToBePromoted(
                    selectedElementData,
                    isBaseContentPromoteDemoteAllowed!
                ) &&
                input?.action === 'Promote'
            ) {
                const newOrder = shouldSetParentAsUndefinedForSustainabilityPromotion
                    ? parent
                    : parentElementData?.children.find(
                          (itemContent) =>
                              itemContent.id ===
                                  selectedElementData.documentTemplateViewItem
                                      ?.itemId ||
                              itemContent.id === selectedElementData?.parentId
                      )
                order = newOrder?.order! + 1
            } else {
                return defaultPromoteDemoteSnackBar()
            }

            // Check if content is movable to new parent
            // For sustainability topic or new content, we dont throw this error
            if (
                !isContentMovableToParent(parentElementData) &&
                !shouldSetParentAsUndefinedForSustainabilityPromotion &&
                !selectedElementData.isUserAdded
            ) {
                return defaultPromoteDemoteSnackBar(
                    PromoteDemoteBaseContentError
                )
            }

            // Check content if causes key duplicates
            const checkContentCauseKeyDuplicatesRequirements: CheckContentCauseKeyDuplicatesRequirements = {
                content: selectedElementData,
                newParentElementID: parentElementData?.itemId || '',
                articlesData: articlesData,
            }
            if (
                checkContentCauseKeyDuplicates(
                    checkContentCauseKeyDuplicatesRequirements
                )
            ) {
                return defaultPromoteDemoteSnackBar(DuplicateKeyErrorMessage)
            }

            // Set and run promote/demote
            const promoteDemoteContentItemRequirements: PromoteDemotContentItemRequirements = {
                contentId:
                    selectedElementData?.documentTemplateViewItem?.itemId,
                documentTemplateViewId:
                    selectedElementData?.documentTemplateView?.id,
                contentItemId: selectedElementData?.contentItem?.id,
                parentContentId:
                    parentElementData?.documentTemplateViewItem?.itemId,
                parentContentItemId: parentElementData?.contentItem?.id,
                productFamilyId: projectProductFamilyId,
                order: order,
                projectNuggetId: nugget?.id,
                elementType: !parentElementData ? 'SUSTAINABILITY' : undefined,
                topicContentItemsDetail: getTopicContentItemsDetailForSustainabilityContent(
                    workspaceTemplateData,
                    dtv?.items
                ),
            }
            promoteDemoteContentItem({
                variables: {
                    promoteDemonteContentInput: promoteDemoteContentItemRequirements,
                },
            })
        },
        [
            defaultPromoteDemoteSnackBar,
            itemChildren,
            nugget,
            projectProductFamilyId,
            promoteDemoteContentItem,
            promoteDemoteLoading,
            workspaceTemplateData,
            dtv,
        ]
    )

    //#endregion

    const [storeIsSharedVisible] = useAELocalStorageWriteOnly({
        key: `${selectedProjectNode?.id}-${selectedProjectNode?.baseElementId}-isSharedVisible`,
        userId: kpUserId,
        projectId: projectId as string,
    })

    const setAreSharedElementsVisible = React.useCallback(
        (visible: boolean) => {
            storeIsSharedVisible(type === 'All' ? true : visible)
            setAreSharedPropertiesVisible(type === 'All' ? true : visible) // update reactive var so UI updates ("Show shared properties" toggle)
        },
        [storeIsSharedVisible, type, setAreSharedPropertiesVisible]
    )

    React.useEffect(() => {
        let filteredChildren = item?.children

        if (!isContentItemReOrdered) {
            filteredChildren =
                type === 'Family' || type === 'Sustainability Topic'
                    ? filteredChildren
                    : filteredChildren?.filter((eachItem) =>
                          isBPWorkspace || !areSharedVisible
                              ? !eachItem.isSharedProductTypeProperty
                              : true
                      )
        }
        if (!isTrackChangeToggleOn) {
            filteredChildren = filteredChildren?.filter(
                (article) =>
                    article?.contentItem?.elements?.[0].changeType?.toLowerCase() !==
                    TrackChangeType.DELETED.toLowerCase()
            )
        }
        setItemChildren(filteredChildren)

        handleIsContentItemReOrdered?.(false)
    }, [
        type,
        areSharedVisible,
        item,
        isBPWorkspace,
        isContentItemReOrdered,
        handleIsContentItemReOrdered,
        isTrackChangeToggleOn,
    ])

    React.useEffect(() => {
        if (
            type === 'All' &&
            articlesOnProductFamily &&
            articlesOnProductFamily?.length !== 0
        ) {
            setAllFullViewArticles(articlesOnProductFamily)
        }
    }, [type, articlesOnProductFamily])
    React.useEffect(() => {
        if (tab !== localTab) {
            setLocalTab(tab ?? 0)
        }
    }, [tab, localTab])

    const updatedGeneralItems = React.useMemo(() => {
        if (isProductTypeElement) {
            return allFullViewArticles?.filter(
                (item) => item?.partType === 'General'
            )
        } else {
            return general?.children ?? []
        }
    }, [allFullViewArticles, isProductTypeElement, general])

    const updatedExecutionItems = React.useMemo(() => {
        if (isProductTypeElement) {
            return allFullViewArticles?.filter(
                (item) => item?.partType === 'Execution'
            )
        } else {
            return execution?.children ?? []
        }
    }, [isProductTypeElement, execution, allFullViewArticles])

    const updatedProductsItems = React.useMemo(() => {
        if (isProductTypeElement) {
            return (
                [
                    ...(products?.children?.map((item) => {
                        if (item.isSharedProductTypeProperty) {
                            return item
                        } else {
                            return {
                                ...item,
                                containManufacturerParagraph: true,
                            }
                        }
                    }) ?? []),
                    ...(allFullViewArticles?.filter(
                        (item) => item?.contentItem?.isProductGroup
                    ) ?? []),
                ]?.filter(
                    (value, index, self) =>
                        index ===
                        self.findIndex(
                            (fmItem) =>
                                fmItem?.contentItem?.elements?.[0]?.text ===
                                value?.contentItem?.elements?.[0]?.text
                        )
                ) ?? []
            )
        } else {
            return products?.children ?? []
        }
    }, [isProductTypeElement, products, allFullViewArticles])
    React.useEffect(() => {
        if (loading) {
            setItemChildren(undefined)
        }
        if (type === 'All' && documentTemplateView) {
            setItem({
                id: 'tempId',
                level: 0,
                children: [
                    ...(updatedGeneralItems ?? []),
                    ...(updatedProductsItems ?? []),
                    ...(updatedExecutionItems ?? []),
                ],
            })
        }
        if (
            (type === 'Family' || type === 'Sustainability Topic') &&
            documentTemplateView
        ) {
            if (localTab === 0) setItem(general)
            else if (localTab === 1) setItem(products)
            else if (localTab === 2) setItem(execution)
        }

        if (type === 'ProductType' && documentTemplateView) {
            if (localTab === 0) setItem(general)
            else if (localTab === 1) setItem(products)
            else if (localTab === 2) setItem(execution)
        }
    }, [
        general,
        products,
        execution,
        loading,
        localTab,
        type,
        documentTemplateView,
        updatedGeneralItems,
        updatedProductsItems,
        updatedExecutionItems,
    ])
    const expandCollapseWrapper = (
        <GridItem item className={classes.expandCollapseContainer} xs>
            {type === 'ProductType' && localTab === 1 && (
                <PremiumInfoIconTooltip
                    open={isTooltipOpen}
                    size="medium"
                    mode={
                        selectedProjectNode?.isInProject
                            ? 'workspaceAdded'
                            : 'workspace'
                    }
                    iconClassName={classes.iconContainer}
                    onClick={handleClickPremiumIcon}
                    onClickOutside={handleClickOutsidePremiumIcon}
                />
            )}
            <Button
                className={clsx(
                    classes.expandCollapseButton,
                    'expand-all-button'
                )}
                data-testid="btnExpandAll"
                variant="outlined"
                endIcon={<KeyboardArrowDownIcon />}
                onClick={handleExpandAll}
                classes={{
                    label: classes.buttonLabel,
                }}
            >
                Expand All
            </Button>
            <Button
                className={clsx(
                    classes.expandCollapseButton,
                    'collapse-all-button'
                )}
                data-testid="btnCollapseAll"
                variant="outlined"
                endIcon={<KeyboardArrowUpIcon />}
                onClick={handleCollapseAll}
                classes={{
                    label: classes.buttonLabel,
                }}
            >
                Collapse All
            </Button>
        </GridItem>
    )

    return (
        <div className={classes.container} data-testid="dvCustomTabs">
            {selectedProjectNode && !selectedTopic && (
                <GridContainer
                    direction="row"
                    className={clsx(
                        classes.tabContainer,
                        type !== 'All' && contentTeamFromStudioPage
                            ? classes.tabContainerOM
                            : classes.tabsSMMFullView
                    )}
                >
                    {expandCollapseWrapper}

                    <GridItem item xs>
                        {type !== 'All' && (
                            <Tabs
                                value={tab}
                                onChange={handleChange}
                                aria-label="product-family"
                                variant="fullWidth"
                                scrollButtons="off"
                                selectionFollowsFocus
                                classes={{
                                    fixed: classes.tabsFixed,
                                    indicator: classes.tabsIndicator,
                                }}
                            >
                                <Tab
                                    label="General"
                                    {...a11yProps(0)}
                                    className={clsx(
                                        tab === 0 && classes.tabActive
                                    )}
                                    classes={{
                                        root: classes.tabRoot,
                                    }}
                                />
                                <Tab
                                    label="Products"
                                    {...a11yProps(1)}
                                    className={clsx(
                                        tab === 1 && classes.tabActive
                                    )}
                                    classes={{
                                        root: classes.tabRoot,
                                    }}
                                />
                                <Tab
                                    label="Execution"
                                    {...a11yProps(2)}
                                    className={clsx(
                                        tab === 2 && classes.tabActive
                                    )}
                                    classes={{
                                        root: classes.tabRoot,
                                    }}
                                />
                            </Tabs>
                        )}
                    </GridItem>
                </GridContainer>
            )}
            {
                <div
                    className={clsx(
                        type === 'All' && classes.contentContainerFullView,
                        type === 'Sustainability Topic' && localTab === 0
                            ? classes.contentContainerTopic
                            : classes.contentContainer,
                        type === 'ProductType' &&
                            (itemChildren?.length ?? 0) === 0 &&
                            classes.contentContainerCenter
                    )}
                    data-testid="formContentArea"
                    id={'droppable-area'}
                >
                    {getNotUsed()}

                    {loading && (
                        <GridContainer
                            direction="column"
                            className={classes.loadingSkeletonContainer}
                        >
                            {[...Array(4)].map((_, index) => (
                                <GridItem
                                    key={index}
                                    className={classes.loadingSkeletonItem}
                                >
                                    {/* Article content items skeleton */}
                                    <div
                                        className={
                                            classes.loadingSkeletonContent
                                        }
                                    >
                                        <div
                                            className={
                                                classes.loadingSkeletonLine
                                            }
                                        >
                                            <LoadingSkeleton height={16} />
                                        </div>
                                        <div
                                            className={
                                                classes.loadingSkeletonLine
                                            }
                                        >
                                            <LoadingSkeleton height={16} />
                                        </div>
                                        <div
                                            className={
                                                classes.loadingSkeletonLine
                                            }
                                        >
                                            <LoadingSkeleton height={16} />
                                        </div>
                                    </div>
                                </GridItem>
                            ))}
                        </GridContainer>
                    )}

                    {!loading &&
                        ([
                            'Sustainability Topic',
                            'Family',
                            'ProductType',
                        ].includes(type) ? (
                            <DragDropAreaProvider
                                articles={
                                    type !== 'Sustainability Topic'
                                        ? itemChildren ?? []
                                        : []
                                }
                                tabData={
                                    item as NormalizedDocumentTemplateViewItem
                                }
                                setAutoCollapsedId={setAutoCollapsedId}
                            >
                                <DragDropArea
                                    items={itemChildren ?? []}
                                    baseNotes={baseNotes}
                                    handleDragEnd={handleDragEnd}
                                    contentTeamFromStudioPage={
                                        contentTeamFromStudioPage
                                    }
                                    part={
                                        type === 'Sustainability Topic'
                                            ? 'ContentGroup'
                                            : undefined
                                    }
                                    selectedTopic={selectedTopic}
                                    isDraftMode={isDraftMode}
                                    setPromoteDemoteRequirements={
                                        setPromoteDemoteRequirements
                                    }
                                    setAreSharedElementsVisible={
                                        setAreSharedElementsVisible
                                    }
                                    selectedContent={selectedContent}
                                    tabSelected={tab}
                                    isAddProhibited={isAddProhibited}
                                    dtv={dtv}
                                    addQueueSib={addQueueSib}
                                    contentItemNodes={contentItemNodes}
                                    elementIdOnEdit={elementIdOnEdit}
                                    isAddOngoing={isAddOngoing}
                                    isNewAddOnQueue={isNewAddOnQueue}
                                    projectProductFamilyId={
                                        projectProductFamilyId
                                    }
                                    familyAddedProductTypes={
                                        familyAddedProductTypes
                                    }
                                />
                            </DragDropAreaProvider>
                        ) : (
                            <>
                                <>
                                    <GridItem
                                        className={
                                            classes.expandCollapseSMMWrapper
                                        }
                                    >
                                        <Typography
                                            className={classes.partLabel}
                                        >
                                            Part 1 General
                                        </Typography>
                                    </GridItem>
                                    {allFullViewArticles &&
                                    allFullViewArticles?.length > 0 ? (
                                        <DragDropAreaProvider
                                            articles={updatedGeneralItems ?? []}
                                            tabData={
                                                {
                                                    ...item,
                                                    children:
                                                        updatedGeneralItems ??
                                                        [],
                                                } as NormalizedDocumentTemplateViewItem
                                            }
                                            setAutoCollapsedId={
                                                setAutoCollapsedId
                                            }
                                            typeForFullView="GENERAL"
                                        >
                                            <DragDropArea
                                                items={
                                                    updatedGeneralItems ?? []
                                                }
                                                setAreSharedElementsVisible={
                                                    setAreSharedElementsVisible
                                                }
                                                baseNotes={baseNotes}
                                                handleDragEnd={handleDragEnd}
                                                contentTeamFromStudioPage={
                                                    contentTeamFromStudioPage
                                                }
                                                selectedTopic={selectedTopic}
                                                isDraftMode={isDraftMode}
                                                setPromoteDemoteRequirements={
                                                    setPromoteDemoteRequirements
                                                }
                                                selectedContent={
                                                    selectedContent
                                                }
                                                tabSelected={tab}
                                                isAddProhibited={
                                                    isAddProhibited
                                                }
                                                dtv={dtv}
                                                addQueueSib={addQueueSib}
                                                contentItemNodes={
                                                    contentItemNodes
                                                }
                                                elementIdOnEdit={
                                                    elementIdOnEdit
                                                }
                                                isAddOngoing={isAddOngoing}
                                                isNewAddOnQueue={
                                                    isNewAddOnQueue
                                                }
                                                projectProductFamilyId={
                                                    projectProductFamilyId
                                                }
                                            />
                                        </DragDropAreaProvider>
                                    ) : (
                                        getNotUsed(true)
                                    )}
                                </>

                                <>
                                    <Typography className={classes.partLabel}>
                                        Part 2 Products
                                    </Typography>
                                    {allFullViewArticles &&
                                    allFullViewArticles?.length > 0 ? (
                                        <DragDropAreaProvider
                                            articles={updatedProductsItems}
                                            tabData={
                                                {
                                                    ...item,
                                                    children:
                                                        updatedProductsItems ??
                                                        [],
                                                } as NormalizedDocumentTemplateViewItem
                                            }
                                            setAutoCollapsedId={
                                                setAutoCollapsedId
                                            }
                                            typeForFullView="PRODUCTS"
                                        >
                                            <DragDropArea
                                                items={
                                                    updatedProductsItems ?? []
                                                }
                                                baseNotes={baseNotes}
                                                handleDragEnd={handleDragEnd}
                                                setAreSharedElementsVisible={
                                                    setAreSharedElementsVisible
                                                }
                                                contentTeamFromStudioPage={
                                                    contentTeamFromStudioPage
                                                }
                                                selectedTopic={selectedTopic}
                                                isDraftMode={isDraftMode}
                                                setPromoteDemoteRequirements={
                                                    setPromoteDemoteRequirements
                                                }
                                                selectedContent={
                                                    selectedContent
                                                }
                                                parent={products}
                                                tabSelected={tab}
                                                isAddProhibited={
                                                    isAddProhibited
                                                }
                                                dtv={dtv}
                                                addQueueSib={addQueueSib}
                                                contentItemNodes={
                                                    contentItemNodes
                                                }
                                                elementIdOnEdit={
                                                    elementIdOnEdit
                                                }
                                                isAddOngoing={isAddOngoing}
                                                isNewAddOnQueue={
                                                    isNewAddOnQueue
                                                }
                                                projectProductFamilyId={
                                                    projectProductFamilyId
                                                }
                                                familyAddedProductTypes={
                                                    familyAddedProductTypes
                                                }
                                            />
                                        </DragDropAreaProvider>
                                    ) : (
                                        getNotUsed(true)
                                    )}
                                </>

                                <>
                                    <Typography className={classes.partLabel}>
                                        Part 3 Execution
                                    </Typography>
                                    {allFullViewArticles &&
                                    allFullViewArticles?.length > 0 ? (
                                        <DragDropAreaProvider
                                            articles={
                                                updatedExecutionItems ?? []
                                            }
                                            tabData={
                                                {
                                                    ...item,
                                                    children:
                                                        updatedExecutionItems ??
                                                        [],
                                                } as NormalizedDocumentTemplateViewItem
                                            }
                                            setAutoCollapsedId={
                                                setAutoCollapsedId
                                            }
                                            typeForFullView="EXECUTION"
                                        >
                                            <DragDropArea
                                                items={
                                                    updatedExecutionItems ?? []
                                                }
                                                baseNotes={baseNotes}
                                                handleDragEnd={handleDragEnd}
                                                contentTeamFromStudioPage={
                                                    contentTeamFromStudioPage
                                                }
                                                setAreSharedElementsVisible={
                                                    setAreSharedElementsVisible
                                                }
                                                selectedTopic={selectedTopic}
                                                isDraftMode={isDraftMode}
                                                setPromoteDemoteRequirements={
                                                    setPromoteDemoteRequirements
                                                }
                                                selectedContent={
                                                    selectedContent
                                                }
                                                tabSelected={tab}
                                                isAddProhibited={
                                                    isAddProhibited
                                                }
                                                dtv={dtv}
                                                addQueueSib={addQueueSib}
                                                contentItemNodes={
                                                    contentItemNodes
                                                }
                                                elementIdOnEdit={
                                                    elementIdOnEdit
                                                }
                                                isAddOngoing={isAddOngoing}
                                                isNewAddOnQueue={
                                                    isNewAddOnQueue
                                                }
                                                projectProductFamilyId={
                                                    projectProductFamilyId
                                                }
                                            />
                                        </DragDropAreaProvider>
                                    ) : (
                                        getNotUsed(true)
                                    )}
                                </>
                            </>
                        ))}

                    {type === 'ProductType' &&
                        itemChildren !== undefined &&
                        (itemChildren?.length ?? 0) === 0 &&
                        !loading &&
                        !areSharedVisible && (
                            <Typography
                                data-testid="enableSharedProperties"
                                component="span"
                                className={classes.grayText}
                            >
                                Enable Shared Properties to view related content
                                between Product Types.
                            </Typography>
                        )}
                </div>
            }
        </div>
    )
}

export default React.memo(CustomTabs)
